import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import type { SwiperRef } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper/types";
import { Zoom, Keyboard, Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/zoom";
import "swiper/css/navigation";
import { IMAGES_BASE_URL } from "../imagesBaseUrl";

interface GalleryProps {
  location: string;
}

interface GalleryImage {
  src: string;
  description?: string;
}

const PAGE_SIZE = 20;

const Gallery: React.FC<GalleryProps> = ({ location }) => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [slideshow, setSlideshow] = useState(false);
  const swiperRef = useRef<SwiperRef>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const closeModal = () => {
    setActiveIndex(null);
    setSlideshow(false);
  };

  useEffect(() => {
    let mounted = true;

    const loadImages = async () => {
      try {
        const response = await fetch(
          `${IMAGES_BASE_URL}/images/${location}/photos.json`
        );
        if (!response.ok) throw new Error("No manifest found");

        const list = await response.json();

        if (mounted) {
          setImages(
            list.map((item: any) => ({
              src: `${IMAGES_BASE_URL}/images/${location}/${item.file}`,
              description: item.description,
            }))
          );
          setVisibleCount(PAGE_SIZE);
        }
      } catch (err) {
        console.warn("Gallery manifest not found or empty:", err);
        if (mounted) setImages([]);
      }
    };

    loadImages();
    return () => {
      mounted = false;
    };
  }, [location]);

  // Load more thumbnails as the user scrolls near the bottom of the grid
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || visibleCount >= images.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, images.length));
        }
      },
      { rootMargin: "600px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, images.length]);

  useEffect(() => {
    if (activeIndex === null) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [activeIndex]);

  const toggleSlideshow = () => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;

    if (slideshow) {
      swiper.autoplay.stop();
    } else {
      swiper.autoplay.start();
    }
    setSlideshow(!slideshow);
  };

  if (!images.length) {
    return (
      <div style={{ textAlign: "center", padding: "60px", opacity: 0.7 }}>
        No photos for this location yet.
      </div>
    );
  }

  const visibleImages = images.slice(0, visibleCount);

  return (
    <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "12px" }}>
      {/* Masonry Layout */}
      <div className="photo-masonry">
        {visibleImages.map((img, i) => (
          <img
            key={img.src}
            src={img.src}
            loading="lazy"
            onClick={() => setActiveIndex(i)}
            style={{
              width: "100%",
              marginBottom: "14px",
              borderRadius: "12px",
              cursor: "zoom-in",
              boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
              transition: "transform 0.2s ease, filter 0.3s",
              filter: "blur(4px)",
            }}
            onLoad={(e) => (e.currentTarget.style.filter = "blur(0)")}
          />
        ))}
      </div>

      {/* Sentinel for incremental loading — invisible, triggers the next batch */}
      {visibleCount < images.length && <div ref={sentinelRef} style={{ height: "1px" }} />}

      {/* Fullscreen Zoom Viewer */}
      {activeIndex !== null && createPortal(
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.97)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Top nav bar — sits above the photo, never overlays it */}
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 20px",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              zIndex: 10,
            }}
          >
            <span
              style={{
                color: "#fff",
                fontSize: "14px",
                fontWeight: 600,
                background: "#27272a",
                padding: "8px 14px",
                borderRadius: "999px",
              }}
            >
              {activeIndex + 1} / {images.length}
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <button
                onClick={toggleSlideshow}
                style={{
                  padding: "10px 18px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#27272a",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                {slideshow ? "Stop" : "Play"}
              </button>

              <button
                onClick={closeModal}
                style={{
                  padding: "10px 18px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#27272a",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Close
              </button>
            </div>
          </div>

          <Swiper
            ref={swiperRef}
            modules={[Zoom, Keyboard, Navigation, Autoplay]}
            initialSlide={activeIndex}
            zoom={{ maxRatio: 3 }}
            keyboard={{ enabled: true }}
            navigation
            autoplay={{ delay: 2500, disableOnInteraction: false }}
            onSwiper={(swiper: SwiperClass) => swiper.autoplay.stop()}
            onSlideChange={(swiper: SwiperClass) =>
              setActiveIndex(swiper.activeIndex)
            }
            style={{ flex: 1, width: "100%" }}
          >
            {images.map((img) => (
              <SwiperSlide
                key={img.src}
                zoom
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={img.src}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Caption overlay — sibling of Swiper so it never scales with zoom */}
          {images[activeIndex]?.description && (
            <div
              style={{
                position: "absolute",
                bottom: "24px",
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(0,0,0,0.55)",
                color: "#fff",
                padding: "8px 14px",
                borderRadius: "10px",
                fontSize: "15px",
                maxWidth: "90%",
                textAlign: "center",
                backdropFilter: "blur(4px)",
                pointerEvents: "none",
              }}
            >
              {images[activeIndex].description}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Gallery;
